import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { QueryHookOptions, useLazyQuery, useQuery } from '@apollo/client'
import { Box, Button, Spinner, useToggle } from '@factorialco/deprecated-design-system'
import {
  PayrollResultFragment,
  SendToBookkeeperPayrollRunDocument
} from 'generated/resources/graphql'
import {
  AvailablePayrollConceptFieldFragment,
  CompensationsListFragment,
  CompensationsPayrollResultsDocument,
  CompensationsPayrollRunDocument,
  CompensationsPayrollRunQuery,
  CompensationsPayrollRunQueryVariables,
  CyclesManagementCheckCompensationsPayrollRunDocument
} from 'generated/resources/graphql'
import moment from 'moment'

import Callout from 'modules/core/components/Callout'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import {
  type TriggeredByAction,
  getDaysSinceCycleStart,
  trackCycleStatusChanged
} from 'modules/compensations/lib/cycleStatusTracking'

import { EmployeePayrollRunNode } from 'modules/compensations/components/EmployeePayrollRunElements'
import type ImportPayrollResultModal from 'modules/compensations/components/ImportPayrollResultModal'
import { PayrollRunActions } from 'modules/compensations/components/PayrollRunActions'
import { CompensationsRefreshContext } from 'modules/compensations/lib/CompensationsRefreshContext'
import { useCurrencyContext } from 'modules/compensations/lib/CurrencyContext'
import { usePayrollRuns } from 'modules/compensations/lib/PayrollRunsContext'
import {
  EUR,
  LegacyPayrollRunStatus,
  PaymentRequestStatus,
  PayrollRunStatus,
  getCurrencyByIsoCode
} from 'modules/compensations/lib/constants'
import { isSourceStatus } from 'modules/compensations/lib/payrollRunStatus'
import { useRefetchQueries } from 'modules/compensations/lib/query-utils'
import type { AllFeatureIds } from 'generated/featureIds.generated'
import { isFeatureEnabled } from 'modules/compensations/lib/utils'
import { CompensationsExportAlertModal } from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsExportAlertModal'
import { CompensationsList } from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsList'
import { CompleteCycleModal } from 'modules/compensations/pages/CompensationsPayrollRun/CompleteCycleModal'
import {
  CycleCompensationsList,
  type CycleCompensationsListOnConfirmSync
} from 'modules/compensations/pages/CompensationsPayrollRun/CycleCompensationsList'
import { EditCompensationsFormStandalone } from 'modules/compensations/pages/CompensationsPayrollRun/EditCompensationsForm'
import { CompensationsPayrollRunLayout } from 'modules/compensations/pages/CompensationsPayrollRun/Layout'
import { LockPayrollRunStatusTransitionModal } from 'modules/compensations/pages/CompensationsPayrollRun/LockPayrollRunStatusTransitionModal'
import LockToggleButton from 'modules/compensations/pages/CompensationsPayrollRun/LockToggleButton'
import { PayrollRunEmptyStateCallout } from 'modules/compensations/pages/CompensationsPayrollRun/PayrollRunEmptyStateCallout'
import PayrollRunStatusAlert from 'modules/compensations/pages/CompensationsPayrollRun/PayrollRunStatusAlert'
import TogglePayrollRunLockModal from 'modules/compensations/pages/CompensationsPayrollRun/TogglePayrollRunLockModal'
import { useOptimisticStatusUpdate } from 'modules/compensations/pages/CompensationsPayrollRun/cacheUtils'
import { useCrudDisclosure } from 'modules/contracts/hooks/useCrudDisclosure'
import { SyncFooter, useIntegrationsSyncContext } from 'modules/integrations/public'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'
import { ExportSidepanel } from 'modules/universal_exports/components/ExportSidepanel'

import { ActivitySidepanel } from './ActivitySidepanel'
import CreatePaymentModal from './CreatePaymentModal'
import ManagePayrollResultsModal from './ManagePayrollResultsModal'
import { MissingBankDetailsModal } from './MissingBankDetailsModal'
import PayrollRunDataUpdatedInfo from './PayrollRunDataUpdatedInfo'
import {
  PayrollRunStatusTransitionModal,
  type SourceStatuses,
  isStatusTransition,
  useCalculationToReviewModal
} from './PayrollRunStatusTransitionModal'
import { filterPayrollConcepts } from './payrollConceptsUtils'
import { usePayrollRunPreviousStatus } from './usePayrollRunPreviousStatus'
import CompensationsNotifiedToProfessionalsCallout from 'modules/bookkeepers/components/CompensationsNotifiedToProfessionalsCallout'
import { useBlockingIssuesLazyQuery } from 'modules/integrations/hooks/SyncContext/useBlockingIssuesLazyQuery'
import { BlockingIssuesModal } from 'modules/integrations/components/SyncButton/Modals/BlockingIssuesModal'

function useCompensationsPayrollRunQuery(
  baseOptions: QueryHookOptions<CompensationsPayrollRunQuery, CompensationsPayrollRunQueryVariables>
) {
  const options = { ...baseOptions }
  return useQuery<CompensationsPayrollRunQuery, CompensationsPayrollRunQueryVariables>(
    CompensationsPayrollRunDocument,
    options
  )
}

/**
 * Hook to handle currency updates based on legal entity settings
 */
function useCurrencyUpdate({
  mainLegalEntityCurrencySymbol,
  legalEntityCurrencySymbol,
  isCyclesManagementFeatureEnabled
}: {
  mainLegalEntityCurrencySymbol: string | undefined
  legalEntityCurrencySymbol: string | undefined
  isCyclesManagementFeatureEnabled: boolean
}) {
  const [currency, setCurrency] = useCurrencyContext()

  useEffect(() => {
    if (
      !isCyclesManagementFeatureEnabled &&
      mainLegalEntityCurrencySymbol !== undefined &&
      mainLegalEntityCurrencySymbol !== currency.isoCode &&
      mainLegalEntityCurrencySymbol !== currency.symbol
    ) {
      setCurrency(getCurrencyByIsoCode(mainLegalEntityCurrencySymbol))
    } else if (isCyclesManagementFeatureEnabled && legalEntityCurrencySymbol) {
      setCurrency(getCurrencyByIsoCode(legalEntityCurrencySymbol))
    }
  }, [
    mainLegalEntityCurrencySymbol,
    isCyclesManagementFeatureEnabled,
    legalEntityCurrencySymbol,
    currency.isoCode,
    currency.symbol,
    setCurrency
  ])

  return null
}

function CompensationsPayrollRun() {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { id } = useParams()

  const {
    access: { locale },
    company
  } = useCurrentSession()
  const { currentPayrollRun, loading: payrollRunsLoading } = usePayrollRuns()
  const { setContextParameters } = useIntegrationsSyncContext()

  const {
    data: featuresData,
    loading: loadingFeatures,
    error: errorFeatures
  } = useQuery(CyclesManagementCheckCompensationsPayrollRunDocument, {
    variables: {
      companyId: company.id
    }
  })

  const [showBlockingIssuesModal, setShowBlockingIssuesModal] = useState(false)
  const { fetchBlockingIssues, blockingIssues, blockingIssuesLoading } =
    useBlockingIssuesLazyQuery()

  const isCyclesManagementFeatureEnabled = isFeatureEnabled(
    'dev_compensations_cycles_management',
    featuresData?.features?.featuresConnection
  )

  // TODO: we should cache the result of this query since this is gonna be used on CompensationsList
  // We will tackle this in a future PR
  const queryVariables =
    isCyclesManagementFeatureEnabled && id
      ? {
          companyId: company.id,
          id: String(id),
          marketplaceIntegrationDatoId: integrationCodes.a3innuva
        }
      : currentPayrollRun?.id
      ? {
          companyId: company.id,
          id: currentPayrollRun.id,
          marketplaceIntegrationDatoId: integrationCodes.a3innuva
        }
      : undefined

  const { data, loading, error } = useCompensationsPayrollRunQuery({
    skip: isCyclesManagementFeatureEnabled ? !id : !currentPayrollRun?.id,
    variables: queryVariables
  })

  const [isPaymentCreationInProgress, setIsPaymentCreationInProgress] = useState(false)

  const mainLegalEntityCurrencySymbol =
    data?.apiCore.company?.legalEntities.find((el) => el.main)?.currency ?? EUR.symbol
  const legalEntityCurrencySymbol =
    data?.compensations.payrollRunsConnection.nodes[0]?.cycle?.currency || undefined

  useCurrencyUpdate({
    mainLegalEntityCurrencySymbol,
    legalEntityCurrencySymbol,
    isCyclesManagementFeatureEnabled
  })

  const [fetchPayrollResults, { data: payrollResultsData }] = useLazyQuery(
    CompensationsPayrollResultsDocument,
    {
      fetchPolicy: 'network-only'
    }
  )

  const payrollRun = data?.compensations?.payrollRunsConnection.nodes[0]

  const [sendToBookkeeper] = useMutation(SendToBookkeeperPayrollRunDocument, {
    extractorFn: (result) => result.compensationsMutations.sendToBookkeeperCompensationsPayrollRun
  })

  useUnsafeEffect(() => {
    const payrollRunId = payrollRun?.id
    if (!payrollRunId) return

    if (isCyclesManagementFeatureEnabled) {
      setContextParameters({ payroll_run_id: payrollRunId.toString() })
    }
    refreshPayrollResults()
  }, [payrollRun, setContextParameters, isCyclesManagementFeatureEnabled])

  const refreshPayrollResults = () => {
    if (!payrollRun) return

    fetchPayrollResults({
      variables: {
        payrollRunIds: [payrollRun.id],
        orderBy: { field: 'createdAt', order: 'DESC' }
      }
    })
  }

  const hasWebsockets = isFeatureEnabled(
    'dev_compensations_websockets',
    data?.features?.featuresConnection
  )
  const isPayrollRunTimelineFeatureEnabled = isFeatureEnabled(
    'dev_compensations_payrollrun_timeline',
    data?.features?.featuresConnection
  )

  const isCompensationSyncInProfessionalsEnabled = isFeatureEnabled(
    'dev_enable_compensation_sync_in_professionals' as AllFeatureIds,
    data?.features?.featuresConnection
  )

  const isCyclesActivityFeatureEnabled = isFeatureEnabled(
    'dev_payroll_cycles',
    data?.features?.featuresConnection
  )

  const hasProfessionals = isFeatureEnabled(
    'factorial_professionals',
    data?.features?.featuresConnection
  )

  const hasCompensationsProfessionalsConnection = isFeatureEnabled(
    'dev_compensations_professionals_connection',
    data?.features?.featuresConnection
  )

  const canManageBookkeeperRelationship =
    data?.compensations?._policies?.compensationActivityManage?.can ?? false

  const showSendToBookkeeper =
    canManageBookkeeperRelationship &&
    isCyclesActivityFeatureEnabled &&
    hasProfessionals &&
    isPayrollRunTimelineFeatureEnabled &&
    hasCompensationsProfessionalsConnection &&
    payrollRun?.status != PayrollRunStatus.COMPLETED

  const [activityOpened, setActivityOpened] = useState<boolean>()
  const [exportOpened, setExportOpened] = useState<boolean>()
  const [showTogglePayrollRunLockModal, setShowTogglePayrollRunLockModal] = useState<boolean>(false)
  const [
    showPayrollRunFromInPreparationToCalculationStatusTransitionModal,
    setShowPayrollRunFromInPreparationToCalculationStatusTransitionModal
  ] = useState<boolean>(false)
  const [statusBeforeCalculationTransition, setStatusBeforeCalculationTransition] = useState<
    SourceStatuses | undefined
  >(undefined)
  const [showManagePayrollResultsModal, setShowManagePayrollResultsModal] = useState<boolean>(false)
  const [openLockTooltip, toggleLockTooltip] = useToggle(false)
  const [showExportAlertModal, setShowExportAlertModal] = useState(false)
  const [showCompleteCycleModal, setShowCompleteCycleModal] = useState(false)
  const [showCreatePaymentModal, setShowCreatePaymentModal] = useState(false)
  const [showMissingBankDetailsModal, setShowMissingBankDetailsModal] = useState(false)
  const pendingToDefineCount = data?.compensations?.pendingToDefine?.totalCount ?? 0
  const aboveLimitCount = data?.compensations?.aboveLimit?.totalCount ?? 0
  const hasPendingValidations = pendingToDefineCount > 0 || aboveLimitCount > 0
  const payrollRunId = payrollRun?.id
  const status = payrollRun?.status
  const refetchCompensationsQueries = useRefetchQueries([
    'CompensationsListColumns',
    'ValidationTypeFilters',
    'EmployeePayrollRuns'
  ])
  const { updatePayrollRunStatus } = useOptimisticStatusUpdate()

  // Handle IN_REVIEW modal for page refresh scenario (status already persisted)
  // Uses localStorage to show modal only once per payroll run
  const {
    showModal: showPayrollRunFromCalculationToInReviewStatusTransitionModal,
    handleClose: handleCloseCalculationToReviewModal
  } = useCalculationToReviewModal(payrollRunId, status)

  const { storedPrevStatus, statusBeforeInReviewTransition, setStatusBeforeInReviewTransition } =
    usePayrollRunPreviousStatus(
      payrollRunId,
      status,
      payrollRun,
      isPayrollRunTimelineFeatureEnabled
    )

  const cycleCountry = data?.compensations?.payrollRunsConnection.nodes[0]?.cycle?.country || ''

  const trackStatusChange = (
    newStatus: string,
    triggeredByAction: TriggeredByAction,
    previousStatus?: string
  ) => {
    if (!payrollRun) return
    trackCycleStatusChanged({
      cycle_id: String(payrollRun.id),
      company_id: String(company.id),
      previous_status: previousStatus ?? payrollRun.status ?? PayrollRunStatus.OPEN,
      new_status: newStatus,
      triggered_by_action: triggeredByAction,
      cycle_period_start: payrollRun.startsOn,
      cycle_period_end: payrollRun.endsOn,
      days_since_cycle_creation: getDaysSinceCycleStart(payrollRun.startsOn),
      country: cycleCountry
    })
  }

  const handleExportClick = () => {
    if (hasPendingValidations) {
      setShowExportAlertModal(true)
    } else {
      setExportOpened(true)
      track(trackingEvents.compensations.openExportSidePanel)
    }
  }

  const handleConfirmExport = () => {
    setShowExportAlertModal(false)
    setExportOpened(true)
    track(trackingEvents.compensations.openExportSidePanel)
  }

  const handleCancelExport = () => {
    setShowExportAlertModal(false)
  }

  const handleStatusTransitionToCalculation = (): string | undefined => {
    if (
      isPayrollRunTimelineFeatureEnabled &&
      payrollRun &&
      isStatusTransition(payrollRun.status, PayrollRunStatus.CALCULATION)
    ) {
      if (
        payrollRun.status === PayrollRunStatus.IN_REVIEW ||
        payrollRun.status === PayrollRunStatus.COMPLETED
      ) {
        return undefined
      }

      const currentStatus = payrollRun.status
      if (isSourceStatus(currentStatus)) {
        setStatusBeforeCalculationTransition(currentStatus)
      }
      setShowPayrollRunFromInPreparationToCalculationStatusTransitionModal(true)
      updatePayrollRunStatus(String(id))
      return currentStatus || PayrollRunStatus.OPEN
    }
    return undefined
  }

  const handleRefresh = () => {
    if (!payrollRun) return

    refreshPayrollResults()

    refetchCompensationsQueries()
  }

  if (error || errorFeatures) {
    throw new Error('Error fetching data')
  }

  if (loading || payrollRunsLoading || loadingFeatures) {
    return (
      <Box padding="s40" width="full" alignItems="center">
        <Spinner />
      </Box>
    )
  }

  if (!payrollRun) {
    return null
  }

  const payrollResults = payrollResultsData?.payroll?.resultsConnection?.nodes ?? []

  let payrollConcepts = data?.payroll?.conceptsConnection.nodes ?? []

  if (payrollResults.length === 0) {
    payrollConcepts = payrollConcepts.filter(filterPayrollConcepts)
  }

  const availablePayrollConcepts = [...payrollConcepts].sort((a, b) =>
    a.label.localeCompare(b.label)
  )

  const canToggleLock: boolean =
    data?.compensations?._policies?.payrollRunToggleLockStatus?.can ?? false
  const canExportCompensations: boolean =
    data?.compensations?._policies?.employeeCompensationExport?.can ?? false
  const endsOn = stringToDate(payrollRun?.endsOn, 'backend_datetime')
  const lastDayOfMonth = moment().endOf('month').startOf('day')
  const isPastPeriod = endsOn.isBefore(lastDayOfMonth, 'day')
  const isClosed =
    LegacyPayrollRunStatus.CLOSED === payrollRun.status ||
    PayrollRunStatus.COMPLETED === payrollRun.status
  const lockedAtDate = payrollRun.lockedAt
    ? stringToDate(payrollRun.lockedAt, 'backend_datetime')
    : null

  const canImportCompensations =
    data.universalImporter?._policies.importResources.can &&
    data.compensations?._policies?.employeeCompensationBulkImport?.can &&
    payrollRun?.id !== null &&
    payrollRun?.id !== undefined &&
    !isClosed

  const canImportResults =
    data.compensations?._policies?.payrollResultImport?.can &&
    data.universalImporter?._policies.importResources.can &&
    payrollRun?.id !== null &&
    payrollRun?.id !== undefined

  const showImportResults = Boolean(canImportCompensations && canImportResults)

  // Keep type reference to avoid orphan checks
  // TODO: Remove this once we have a proper way to test the ImportPayrollResultModal in
  // Finance workspace in the next PRs

  type _UnusedImportPayrollResultModal = typeof ImportPayrollResultModal

  const handleManagePayrollResultsClick = () => {
    setShowManagePayrollResultsModal(true)
    track(trackingEvents.compensations.clickManagePayrollResults)
  }

  const isLocked = payrollRun.locked
  const shouldLockPayrollRun = !isLocked

  const paymentRequestItemsCount =
    data?.compensations?.paymentRequestItemsConnection?.totalCount ?? 0
  const pendingPaymentCount =
    data?.compensations?.paymentRequestPendingItemsConnection?.totalCount ?? 0
  const paymentRequestItems = data?.compensations?.paymentRequestItemsConnection?.nodes ?? []
  const pendingPaymentItems = data?.compensations?.paymentRequestPendingItemsConnection?.nodes ?? []

  const hasPaymentRequests = paymentRequestItemsCount > 0
  const hasPendingPayments = pendingPaymentCount > 0
  const allPaymentsPaid =
    hasPaymentRequests &&
    !hasPendingPayments &&
    paymentRequestItems.every((item) => item.status === PaymentRequestStatus.PAID)

  const employeesWithoutBankAccount = pendingPaymentItems
    .filter((item) => !item.hasBankAccount && !!item.employee)
    .map((item) => item.employee)
    .filter((emp) => !!emp)

  const canSkip = pendingPaymentItems.length > employeesWithoutBankAccount.length

  const handleCreatePaymentClick = () => {
    if (employeesWithoutBankAccount.length > 0) {
      setShowMissingBankDetailsModal(true)
    } else {
      setShowCreatePaymentModal(true)
    }
  }

  const handleSendToBookkeeperClick = async () => {
    const result = await sendToBookkeeper({
      variables: {
        payrollRunId: payrollRun.id
      }
    })

    const handled = handleResult(result, () => undefined)

    if (
      handled.success &&
      payrollRun &&
      payrollRun.status !== PayrollRunStatus.COMPLETED &&
      isStatusTransition(payrollRun.status, PayrollRunStatus.CALCULATION)
    ) {
      const currentStatus = payrollRun.status
      trackStatusChange(
        PayrollRunStatus.CALCULATION,
        'send_to_bk',
        currentStatus || PayrollRunStatus.OPEN
      )
      if (isSourceStatus(currentStatus)) {
        setStatusBeforeCalculationTransition(currentStatus)
      }
    }
  }

  return (
    <CompensationsPayrollRunLayout
      onActionClick={(key) => {
        switch (key) {
          case 'lock-payroll-run':
            setShowTogglePayrollRunLockModal(true)
            break
          case 'complete-cycle':
            setShowCompleteCycleModal(true)
            break
          case 'toggle-cycle-activity':
            setActivityOpened(true)
            break
          case 'payments':
            if (!id) return
            navigate(navigation.compensations.cycles.id.payments.fullPath({ id }))
            break
          case 'create-payment':
            handleCreatePaymentClick()
            break
        }
      }}
      header={
        <Box gap="s16">
          <PayrollRunEmptyStateCallout payrollRun={payrollRun} />
          {!isPayrollRunTimelineFeatureEnabled && (
            <PayrollRunStatusAlert
              isClosed={isPastPeriod || isClosed}
              lockedAt={lockedAtDate}
              canToggleLock={canToggleLock}
            />
          )}
          {payrollRun && hasWebsockets && (
            <PayrollRunDataUpdatedInfo payrollRunId={payrollRun.id} onRefresh={handleRefresh} />
          )}

          {!isPaymentCreationInProgress && hasPaymentRequests && hasPendingPayments && (
            <Callout
              type="warning"
              compact
              description={i18n.tp('compensations.payments.pending_payment_requests_banner', {
                count: pendingPaymentCount
              })}
              action={
                <Button type="secondary" onClick={handleCreatePaymentClick}>
                  {i18n.t('compensations.payments.send_for_payment')}
                </Button>
              }
            />
          )}
          {allPaymentsPaid && !isClosed && (
            <Callout
              type="info"
              compact
              description={i18n.t('compensations.payments.all_payments_paid_banner')}
            />
          )}
          {isCompensationSyncInProfessionalsEnabled && (
            <CompensationsNotifiedToProfessionalsCallout payrollRunStatus={payrollRun.status} />
          )}
        </Box>
      }
      footer={isCyclesManagementFeatureEnabled ? <SyncFooter /> : undefined}
    >
      {!isCyclesManagementFeatureEnabled && (
        <Box flexDirection="row" gap="s12" alignSelf="flexEnd">
          {payrollRun && canToggleLock && (
            <LockToggleButton
              isClosed={isPastPeriod || isClosed}
              isLocked={isLocked}
              onToggle={() => setShowTogglePayrollRunLockModal(true)}
              openLockTooltip={openLockTooltip}
              toggleLockTooltip={toggleLockTooltip}
            />
          )}
          {data && (
            <PayrollRunActions
              data={data}
              payrollResults={payrollResults}
              onExportClick={handleExportClick}
              onManagePayrollResultsClick={handleManagePayrollResultsClick}
              canExportCompensations={canExportCompensations}
              isClosed={isPastPeriod || isClosed}
              isCyclesManagementFeatureEnabled={isCyclesManagementFeatureEnabled}
            />
          )}
        </Box>
      )}
      {showExportAlertModal && (
        <CompensationsExportAlertModal
          pendingToDefineCount={pendingToDefineCount}
          aboveLimitCount={aboveLimitCount}
          onSuccess={handleConfirmExport}
          onClose={handleCancelExport}
        />
      )}
      {exportOpened && (
        <ExportSidepanel
          resource="compensations.payroll_run_employees_compensation"
          overrideFilters={{ payroll_run_id: [payrollRun.id] }}
          metadata={{ payroll_run_id: [payrollRun.id] }}
          hasDynamicFields
          onClose={() => {
            setExportOpened(false)
          }}
          onExport={() => {
            if (!isPayrollRunTimelineFeatureEnabled && shouldLockPayrollRun && !isClosed) {
              setShowTogglePayrollRunLockModal(true)
            }
            if (
              payrollRun.status !== PayrollRunStatus.OPEN &&
              payrollRun.status !== PayrollRunStatus.COMPLETED
            ) {
              const previousStatus = handleStatusTransitionToCalculation()
              if (previousStatus !== undefined) {
                trackStatusChange(PayrollRunStatus.CALCULATION, 'export', previousStatus)
              }
            }
            track(trackingEvents.compensations.completeExport, {
              payroll_run_id: payrollRun.id,
              payroll_run_locked_at: payrollRun.lockedAt
            })
          }}
          fieldsTitleTooltip={i18n.t(
            'compensation.payroll_run_employees_compensation.exportSidePanel.header_column_tooltip'
          )}
        />
      )}
      {activityOpened && (
        <ActivitySidepanel
          payrollRunId={payrollRun.id}
          onClose={() => setActivityOpened(false)}
          hasProfessionals={hasProfessionals}
        />
      )}
      {payrollRun && (
        <PayrollRunCompensationsList
          payrollRun={payrollRun}
          availablePayrollConcepts={availablePayrollConcepts}
          locale={locale}
          canToggleLock={canToggleLock}
          isCyclesManagementFeatureEnabled={isCyclesManagementFeatureEnabled}
          onOpenExportSidePanel={handleExportClick}
          // TODO: we need to refactor passing down all of this handlers. This is going to be tackled once we make the clean up and remove the FF
          onManagePayrollResultsClick={() => setShowManagePayrollResultsModal(true)}
          payrollResults={payrollResults}
          showImportResults={showImportResults}
          canImportCompensations={canImportCompensations}
          canImportResults={canImportResults}
          showSendToBookkeeper={showSendToBookkeeper}
          onSendToBookkeeperClick={async () => {
            if (isPayrollRunTimelineFeatureEnabled) {
              if (isCompensationSyncInProfessionalsEnabled) {
                await fetchBlockingIssues()

                if (!blockingIssuesLoading && blockingIssues.length === 0) {
                  setShowPayrollRunFromInPreparationToCalculationStatusTransitionModal(true)
                } else setShowBlockingIssuesModal(true)
              } else {
                handleSendToBookkeeperClick()
                setShowPayrollRunFromInPreparationToCalculationStatusTransitionModal(true)
              }
            }
          }}
          onConfirmSync={() => {
            const previousStatus = handleStatusTransitionToCalculation()
            if (previousStatus !== undefined) {
              trackStatusChange(PayrollRunStatus.CALCULATION, 'sync', previousStatus)
            }
          }}
          canExportCompensations={canExportCompensations}
        />
      )}
      {payrollRun && showTogglePayrollRunLockModal && (
        <>
          {isPayrollRunTimelineFeatureEnabled ? (
            <LockPayrollRunStatusTransitionModal
              payrollRun={payrollRun}
              companyId={company.id}
              country={cycleCountry}
              onSuccess={() => {
                setShowTogglePayrollRunLockModal(false)
              }}
              onClose={() => setShowTogglePayrollRunLockModal(false)}
              lock={shouldLockPayrollRun}
            />
          ) : (
            <TogglePayrollRunLockModal
              payrollRun={payrollRun}
              companyId={company.id}
              country={cycleCountry}
              onSuccess={() => {
                setShowTogglePayrollRunLockModal(false)
              }}
              onClose={() => setShowTogglePayrollRunLockModal(false)}
              lock={shouldLockPayrollRun}
            />
          )}
        </>
      )}
      {payrollRun && showCompleteCycleModal && (
        <CompleteCycleModal
          payrollRun={payrollRun}
          companyId={company.id}
          country={cycleCountry}
          onConfirm={() => {
            setShowCompleteCycleModal(false)
          }}
          onClose={() => {
            setShowCompleteCycleModal(false)
          }}
        />
      )}
      {payrollRun && showManagePayrollResultsModal && (
        <ManagePayrollResultsModal
          payrollResults={payrollResults}
          onUpdate={() => {
            refreshPayrollResults()
          }}
          onClose={() => {
            setShowManagePayrollResultsModal(false)
            refreshPayrollResults()
          }}
          onImportNewResultClick={() => {
            setShowManagePayrollResultsModal(false)
            const importResultPath = isCyclesManagementFeatureEnabled
              ? navigation.compensations.cycles.id.importResult.fullPath({
                  id: String(payrollRun.id)
                })
              : navigation.compensations.definition.importResult.fullPath()
            navigate(
              importResultPath.replace(/\*+$/, '').concat(`?payroll-run-id=${payrollRun.id}`)
            )
          }}
          isCyclesManagementFeatureEnabled={isCyclesManagementFeatureEnabled}
          canImportCompensations={canImportCompensations}
        />
      )}
      {payrollRun &&
        isPayrollRunTimelineFeatureEnabled &&
        showPayrollRunFromInPreparationToCalculationStatusTransitionModal && (
          <PayrollRunStatusTransitionModal
            isCompensationSyncInProfessionalsEnabled={isCompensationSyncInProfessionalsEnabled}
            payrollRun={payrollRun}
            targetStatus={PayrollRunStatus.CALCULATION}
            isBookkeeperEnabled={showSendToBookkeeper}
            prevStatus={statusBeforeCalculationTransition}
            onClose={() => {
              setShowPayrollRunFromInPreparationToCalculationStatusTransitionModal(false)
              setStatusBeforeCalculationTransition(undefined)
            }}
            onSend={() => {
              handleSendToBookkeeperClick()
              setShowPayrollRunFromInPreparationToCalculationStatusTransitionModal(false)
            }}
          />
        )}
      {payrollRun &&
        isPayrollRunTimelineFeatureEnabled &&
        showPayrollRunFromCalculationToInReviewStatusTransitionModal && (
          <PayrollRunStatusTransitionModal
            isCompensationSyncInProfessionalsEnabled={isCompensationSyncInProfessionalsEnabled}
            payrollRun={payrollRun}
            targetStatus={PayrollRunStatus.IN_REVIEW}
            isBookkeeperEnabled={showSendToBookkeeper}
            prevStatus={
              statusBeforeInReviewTransition ||
              (storedPrevStatus && isSourceStatus(storedPrevStatus) ? storedPrevStatus : undefined)
            }
            onClose={() => {
              const prevStatus =
                statusBeforeInReviewTransition ||
                (storedPrevStatus && isSourceStatus(storedPrevStatus)
                  ? storedPrevStatus
                  : PayrollRunStatus.CALCULATION)
              trackStatusChange(PayrollRunStatus.IN_REVIEW, 'import_results', prevStatus)
              handleCloseCalculationToReviewModal()
              setStatusBeforeInReviewTransition(undefined)
            }}
            onSend={() => {
              handleSendToBookkeeperClick()
              setShowPayrollRunFromInPreparationToCalculationStatusTransitionModal(false)
            }}
          />
        )}
      {showMissingBankDetailsModal && employeesWithoutBankAccount.length > 0 && (
        <MissingBankDetailsModal
          onClose={() => setShowMissingBankDetailsModal(false)}
          onSkip={() => {
            setShowMissingBankDetailsModal(false)
            setShowCreatePaymentModal(true)
          }}
          employeesWithoutBankAccount={employeesWithoutBankAccount}
          canSkip={canSkip}
        />
      )}
      {payrollRun && showCreatePaymentModal && (
        <CreatePaymentModal
          onClose={() => setShowCreatePaymentModal(false)}
          onSuccess={() => {
            setIsPaymentCreationInProgress(true)
          }}
        />
      )}
      {showBlockingIssuesModal && (
        <BlockingIssuesModal
          onClose={() => setShowBlockingIssuesModal(false)}
          blockingIssues={blockingIssues}
        />
      )}
    </CompensationsPayrollRunLayout>
  )
}

type PayrollRunCompensationsListProps = {
  payrollRun: CompensationsListFragment
  availablePayrollConcepts?: Array<AvailablePayrollConceptFieldFragment>
  locale: string
  canToggleLock?: boolean
  isCyclesManagementFeatureEnabled: boolean
  onOpenExportSidePanel: () => void
  onManagePayrollResultsClick: () => void
  payrollResults: PayrollResultFragment[]
  showImportResults: boolean
  showSendToBookkeeper: boolean
  onSendToBookkeeperClick: () => void
  canImportCompensations?: boolean
  canImportResults?: boolean
  canExportCompensations?: boolean
} & CycleCompensationsListOnConfirmSync

function PayrollRunCompensationsList({
  payrollRun,
  availablePayrollConcepts,
  locale,
  canToggleLock,
  isCyclesManagementFeatureEnabled,
  onConfirmSync,
  onOpenExportSidePanel,
  onManagePayrollResultsClick,
  payrollResults,
  showImportResults,
  canImportCompensations,
  canImportResults,
  showSendToBookkeeper,
  onSendToBookkeeperClick,
  canExportCompensations
}: PayrollRunCompensationsListProps) {
  const { isEditing, onClose, onEdit, editValue } = useCrudDisclosure<EmployeePayrollRunNode>()

  const refreshFnRef = useRef<(() => void) | null>(null)

  const refreshCompensations = useCallback(() => {
    if (refreshFnRef.current) {
      refreshFnRef.current()
    }
  }, [])

  const isClosed =
    LegacyPayrollRunStatus.CLOSED === payrollRun.status ||
    PayrollRunStatus.COMPLETED === payrollRun.status
  const readonly = (payrollRun.locked && !canToggleLock) || isClosed

  return (
    <CompensationsRefreshContext.Provider value={{ refreshCompensations }}>
      <Box flexDirection="column" height="full">
        {!isCyclesManagementFeatureEnabled ? (
          <CompensationsList
            payrollRun={payrollRun}
            onEdit={onEdit}
            locale={locale}
            refreshFnRef={refreshFnRef}
          />
        ) : (
          <CycleCompensationsList
            payrollRun={payrollRun}
            onEdit={onEdit}
            locale={locale}
            onOpenExportSidePanel={onOpenExportSidePanel}
            onManagePayrollResultsClick={onManagePayrollResultsClick}
            payrollResults={payrollResults}
            showImportResults={showImportResults}
            canImportCompensations={canImportCompensations}
            canImportResults={canImportResults}
            refreshFnRef={refreshFnRef}
            onConfirmSync={onConfirmSync}
            showSendToBookkeeper={showSendToBookkeeper}
            onSendToBookkeeperClick={onSendToBookkeeperClick}
            canExportCompensations={canExportCompensations}
          />
        )}

        <Sidepanel
          open={isEditing}
          title={
            isEditing && editValue
              ? `${i18n.t('compensations.PayrollRunCompensationsList.titleCompensationsOf')} ${
                  editValue?.employee?.firstName
                } ${editValue?.employee?.lastName}`
              : null
          }
          onClose={onClose}
          closeLabel="Close"
          width="wide"
          boxPadding={false}
        >
          {isEditing && editValue?.employee?.id && availablePayrollConcepts && (
            <EditCompensationsFormStandalone
              availablePayrollConcepts={availablePayrollConcepts}
              employeeId={editValue.employee.id}
              payrollRunId={payrollRun.id}
              readonly={readonly}
            />
          )}
        </Sidepanel>
      </Box>
    </CompensationsRefreshContext.Provider>
  )
}

export { CompensationsPayrollRun }
